import { create } from "zustand";

export interface ICoverageStore {
  coverageSelection: Record<number, number[]>;
  deletedStates: number[];
  selectionResetCount: number;
  setCoverageSelection: (selection: Record<number, number[]>) => void;
  updateStateCities: (stateId: number, cityIds: number[]) => void;
  clearCoverageSelection: () => void;
  markStateAsDeleted: (stateId: number) => void;
  unmarkStateAsDeleted: (stateId: number) => void;
  clearDeletedStates: () => void;
  coverageMode: "select" | "draw";
  coverageModeConfirmed: boolean;
  setCoverageMode: (mode: "select" | "draw") => void;
  coverageResourceId: string | null;
  setCoverageResourceId: (id: string | null) => void;
  coveragePayload: any;
  setCoveragePayload: (payload: any) => void;
  coverageHandleNext: (() => Promise<boolean>) | null;
  registerCoverageHandleNext: (fn: () => Promise<boolean>) => void;
  drawnPolygonData: number[][][];
  setDrawnPolygonData: (data: number[][][]) => void;
  polygonCitySelections: Record<number, number[]>;
  setPolygonCitySelections: (selections: Record<number, number[]>) => void;
  pendingDeletedBackendCoords: number[][][];
  addPendingDeletedBackendCoord: (coord: number[][]) => void;
  clearPendingDeletedBackendCoords: () => void;
  resetCoverageStore: () => void;
  resetAfterSubmit: () => void;
}

export const useCoverageStore = create<ICoverageStore>((set) => ({
  coverageSelection: {},
  deletedStates: [],
  selectionResetCount: 0,
  coverageMode: "select",
  coverageModeConfirmed: false,
  coverageResourceId: null,
  coveragePayload: null,
  coverageHandleNext: null,
  drawnPolygonData: [],
  polygonCitySelections: {},
  pendingDeletedBackendCoords: [],
  setCoverageSelection: (selection) => set({ coverageSelection: selection }),
  updateStateCities: (stateId, cityIds) =>
    set((state) => ({
      coverageSelection: {
        ...state.coverageSelection,
        [stateId]: cityIds,
      },
    })),
  clearCoverageSelection: () =>
    set((state) => ({
      coverageSelection: {},
      deletedStates: [],
      drawnPolygonData: [],
      polygonCitySelections: {},
      pendingDeletedBackendCoords: [],
      selectionResetCount: state.selectionResetCount + 1,
    })),
  markStateAsDeleted: (stateId) =>
    set((state) => ({
      deletedStates: state.deletedStates.includes(stateId)
        ? state.deletedStates
        : [...state.deletedStates, stateId],
    })),

  unmarkStateAsDeleted: (stateId) =>
    set((state) => ({
      deletedStates: state.deletedStates.filter((id) => id !== stateId),
    })),

  clearDeletedStates: () => set({ deletedStates: [] }),
  setCoverageMode: (mode) => set({ coverageMode: mode, coverageModeConfirmed: true }),
  setCoverageResourceId: (id) => set({ coverageResourceId: id }),
  setCoveragePayload: (payload) => set({ coveragePayload: payload }),
  registerCoverageHandleNext: (fn) =>
    set((state) => (state.coverageHandleNext === fn ? state : { coverageHandleNext: fn })),
  setDrawnPolygonData: (data) => set({ drawnPolygonData: data }),
  setPolygonCitySelections: (selections) => set({ polygonCitySelections: selections }),
  addPendingDeletedBackendCoord: (coord) =>
    set((state) => ({
      pendingDeletedBackendCoords: [...state.pendingDeletedBackendCoords, coord],
    })),
  clearPendingDeletedBackendCoords: () => set({ pendingDeletedBackendCoords: [] }),
  resetCoverageStore: () =>
    set({
      coverageSelection: {},
      deletedStates: [],
      selectionResetCount: 0,
      coverageMode: "select",
      coverageModeConfirmed: false,
      coverageResourceId: null,
      coveragePayload: null,
      drawnPolygonData: [],
      polygonCitySelections: {},
      pendingDeletedBackendCoords: [],
    }),
  resetAfterSubmit: () =>
    set({
      coverageSelection: {},
      deletedStates: [],
      drawnPolygonData: [],
      coveragePayload: null,
      selectionResetCount: 0,
      polygonCitySelections: {},
      pendingDeletedBackendCoords: [],
    }),
}));
